// File: webelieve-frontend/src/features/intake/api/intakeCrmApi.ts
// EF-2.8: Frontend API client for CRM pipeline endpoints

import { apiClient } from '@/config/api';
import type { LeadCaseModules } from '../lib/leadCaseModules';
import type { LeadMatterPlan, LeadPotentialMatterPlan } from '../lib/leadIntelligence';

// --- Types ---

export interface LeadSummary {
  clientId: string;
  clientName: string;
  email?: string;
  phone?: string;
  status: string;
  source?: string;
  practiceArea?: string;
  assignedToUserId?: string;
  assignedToName?: string;
  tags?: string[];
  createdAt: string;
  updatedAt: string;
  lastContactDate?: string;
  contactAttempts: number;
  documentCount: number;
  aiClassification?: any;
  leadScore?: number;
}

export interface LeadAddress {
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

export interface LeadConflictParty {
  id?: string;
  name: string;
  role:
    | 'client'
    | 'alias'
    | 'former_name'
    | 'opposing_party'
    | 'opposing_counsel'
    | 'spouse_or_partner'
    | 'ex_spouse'
    | 'employer'
    | 'affiliate'
    | 'witness'
    | 'expert'
    | 'other';
  entityType?: 'person' | 'organization' | 'government' | 'unknown';
  source?: 'profile' | 'document' | 'ai' | 'manual';
  notes?: string;
}

export interface LeadProfile {
  preferredName?: string;
  primaryEmail?: string;
  primaryPhone?: string;
  alternatePhone?: string;
  homeAddress?: LeadAddress;
  preferredContactMethod?: string;
  preferredContactTime?: string;
  language?: string;
  interpreterNeeded?: boolean;
  dateOfBirth?: string;
  maritalStatus?: string;
  educationLevel?: string;
  numberOfDependents?: number;
  numberOfKids?: number;
  employed?: boolean;
  employerName?: string;
  jobTitle?: string;
  practiceArea?: string;
  practiceAreas?: string[];
  shortSummary?: string;
  stateCode?: string;
  jurisdictionCode?: string;
  jurisdictionCourtName?: string;
  incidentDate?: string;
  incidentLocation?: string;
  jurisdiction?: string;
  opposingPartyNames?: string;
  currentOrPriorAttorney?: string;
  spouseOrPartnerName?: string;
  employerOrCompanyName?: string;
  conflictParties?: LeadConflictParty[];
  caseModules?: LeadCaseModules;
  matterPlans?: LeadMatterPlan[];
}

export interface LeadProfileCompleteness {
  completeCount: number;
  totalCount: number;
  percent: number;
  missingFields: string[];
}

export interface LeadListResult {
  leads: LeadSummary[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface LeadDetail extends LeadSummary {
  intakeNotes?: string;
  intakeFormResponses?: Record<string, any>;
  contactInfo?: {
    email?: string;
    phone?: string;
    address?: string;
    alternatePhone?: string;
    preferredContactMethod?: string;
    preferredContactTime?: string;
    dateOfBirth?: string;
    interpreterNeeded?: boolean;
    homeAddress?: LeadAddress;
  };
  leadProfile?: LeadProfile;
  profileCompleteness?: LeadProfileCompleteness;
  leadIntelligence?: {
    summary: string;
    keyFacts: string[];
    riskSignals: string[];
    missingFacts: string[];
    recommendedActions: string[];
    recommendedPracticeArea?: string;
    confidence?: number;
    urgencyLevel?: string;
    splitRecommended: boolean;
    potentialMatters: LeadPotentialMatterPlan[];
  };
  operationalProjection?: {
    conflict_check_posture?: {
      conflict_check_requested: boolean;
      conflict_check_completed: boolean;
      conflict_check_result: string | null;
      latest_conflict_event_at: string | null;
      review_requested?: boolean;
      review_note?: string | null;
      review_task_id?: string | null;
      review_task_status?: string | null;
      review_assignee_user_id?: string | null;
      disposition_status?: string | null;
      disposition_note?: string | null;
      disposition_decided_at?: string | null;
      disposition_decided_by_user_id?: string | null;
      licensing_review_required?: boolean;
      policy_posture?: string | null;
      licensed_states?: string[];
      out_of_state_codes?: string[];
      matched_records?: number;
      conflict_type?: string | null;
      risk_level?: number | null;
      findings_summary?: string | null;
      matched_clients?: Array<{
        client_id: string;
        client_name: string;
        conflict_reason: string;
        similarity: number;
      }>;
      matched_matters?: Array<{
        matter_id: string;
        client_name: string;
        matter_description: string;
        conflict_reason: string;
        similarity: number;
      }>;
      recommendations?: string[];
      structured_parties?: LeadConflictParty[];
      document_signals?: Array<{
        source: string;
        value: string;
        confidence: string;
      }>;
      document_suggested_parties?: LeadConflictParty[];
      diff_summary?: string | null;
      diff?: {
        risk_delta?: number;
        added_client_matches?: number;
        removed_client_matches?: number;
        added_matter_matches?: number;
        removed_matter_matches?: number;
      };
      waiver_stub?: {
        template_id: string;
        status: string;
        prepared_at?: string | null;
        replaceable?: boolean;
      } | null;
      screen_stub?: {
        template_id: string;
        status: string;
        prepared_at?: string | null;
        replaceable?: boolean;
      } | null;
      prospective_client_memory?: {
        current_status?: string;
        was_rejected_or_inactive?: boolean;
      } | null;
      external_enrichment_completed?: boolean;
      latest_external_enrichment_at?: string | null;
      external_enrichment_summary?: string | null;
      external_signals?: Array<{
        title: string;
        signal_type: string;
        summary: string;
        source_url?: string;
        source_label?: string;
        relevance_score?: number;
        relevance_rationale?: string;
        exact_name_match_count?: number;
        exact_name_match_term?: string;
      }>;
      social_summary?: string | null;
      social_signals?: Array<{
        title: string;
        signal_type: string;
        summary: string;
        source_url?: string;
        source_label?: string;
        relevance_score?: number;
        relevance_rationale?: string;
        exact_name_match_count?: number;
        exact_name_match_term?: string;
      }>;
      external_sources?: Array<{
        label?: string;
        url: string;
      }>;
      social_sources?: Array<{
        label?: string;
        url: string;
      }>;
      external_disclaimer?: string | null;
      external_enrichment_status?: string | null;
      external_current_query_label?: string | null;
      external_completed_queries?: number;
      external_total_queries?: number;
      external_average_relevance_score?: number | null;
      social_average_relevance_score?: number | null;
      external_search_log?: Array<{
        id: string;
        label: string;
        tool_id: string;
        display_query: string;
        status: string;
        started_at: string | null;
        completed_at: string | null;
        result_count: number;
        result_summary: string | null;
        preview_results: Array<{
          title: string;
          url?: string;
          snippet?: string;
          source_label?: string;
        }>;
        source_links: Array<{
          label?: string;
          url: string;
        }>;
      }>;
    };
    consultation_posture?: {
      consultation_scheduled: boolean;
      consultation_event_id: string | null;
      consultation_calendar_id: string | null;
      consultation_calendar_name: string | null;
      consultation_title: string | null;
      scheduled_for_user_id: string | null;
      scheduled_for_user_name?: string | null;
      start_time: string | null;
      end_time: string | null;
      location: string | null;
      meeting_mode: string | null;
      prep_task_id: string | null;
      latest_consultation_event_at: string | null;
    };
    retainer_posture?: {
      retainer_email_sent: boolean;
      retainer_document_prepared: boolean;
      retainer_signed: boolean;
      latest_retainer_event_at: string | null;
      retainer_requested?: boolean;
      status?: string | null;
      requested_amount?: number | null;
      collected_amount?: number | null;
      outstanding_amount?: number | null;
      due_date?: string | null;
      last_requested_at?: string | null;
      latest_payment_at?: string | null;
      trust_account_id?: string | null;
      trust_account_name?: string | null;
      trust_ledger_id?: string | null;
      retainer_bill_id?: string | null;
      retainer_bill_number?: string | null;
      retainer_bill_status?: string | null;
      allocated_matter_id?: string | null;
      reminder_needed?: boolean;
      notes?: string | null;
    };
    missing_intake_items?: string[];
    recommended_next_action?: string | null;
  };
  documents: LeadDocument[];
  activity: LeadActivityEvent[];
  synopsis?: any;
}

export interface LeadDocument {
  documentId: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  uploadedAt: string;
  processingStatus: string;
  matterId?: string | null;
}

export interface LeadActivityEvent {
  eventId: string;
  eventType: string;
  description: string;
  source: string;
  triggeredByUserId?: string;
  eventData?: any;
  createdAt: string;
}

export interface LeadListFilters {
  status?: string;
  practice_area?: string;
  assigned_to?: string;
  search?: string;
  source?: string;
  sort_by?: string;
  sort_order?: string;
  page?: number;
  limit?: number;
}

export interface PipelineMetrics {
  statusCounts: Record<string, number>;
  stageCounts: Record<string, number>;
  conversionRate: string;
  totalLeads: number;
  totalConverted: number;
  recentActivityCount: number;
}

export interface CourtRegistryState {
  state_code: string;
  court_count: number;
}

export interface CourtRegistryCourt {
  jurisdiction_code: string;
  court_name: string;
  court_type?: string;
  base_url?: string | null;
  lifecycle_status?: string;
}

export interface ConversionRequest {
  practice_area: string;
  description?: string;
  jurisdiction?: string;
  task_template_id?: string;
  responsible_attorney_id?: string;
  originating_attorney_id?: string;
  responsible_staff_id: string;
  important_dates?: {
    filing_date?: string;
    service_date?: string;
    statute_of_limitations?: string;
  };
  billing_method?: string;
  is_billable?: boolean;
  priority?: string;
}

export interface ConversionResult {
  clientId: string;
  clientName: string;
  clientStatus: string;
  matterId: string;
  matterNumber: string;
  matterStage: string;
  practiceArea: string;
  documentsLinked: number;
  tasksCreated: number;
  templateUsed: string | null;
  conversionTimestamp: string;
  warnings?: string[];
}

const readConversionString = (...values: Array<unknown>): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return undefined;
};

const readConversionNumber = (...values: Array<unknown>): number => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }
  return 0;
};

const normalizeConversionResult = (value: any): ConversionResult => {
  const payload = value?.data && !value?.matterId && !value?.matter_id ? value.data : value;
  const matterId = readConversionString(
    payload?.matterId,
    payload?.matter_id,
    payload?.matter?.matter_id,
    payload?.matter?.id,
  );
  const clientId = readConversionString(
    payload?.clientId,
    payload?.client_id,
    payload?.client?.client_id,
    payload?.client?.id,
  );

  if (!matterId) {
    throw new Error('Lead converted, but the server did not return a matter ID. Refresh the lead and try opening the matter from the Matters list.');
  }
  if (!clientId) {
    throw new Error('Lead converted, but the server did not return a client ID. Refresh the intake dashboard before continuing.');
  }

  return {
    clientId,
    clientName: readConversionString(payload?.clientName, payload?.client_name, payload?.client?.client_name, payload?.client?.name) || 'Converted client',
    clientStatus: readConversionString(payload?.clientStatus, payload?.client_status, payload?.client?.status) || 'converted',
    matterId,
    matterNumber: readConversionString(payload?.matterNumber, payload?.matter_number, payload?.matter?.matter_number) || 'New Matter',
    matterStage: readConversionString(payload?.matterStage, payload?.matter_stage, payload?.matter?.stage) || 'active',
    practiceArea: readConversionString(payload?.practiceArea, payload?.practice_area, payload?.matter?.practice_area) || '',
    documentsLinked: readConversionNumber(payload?.documentsLinked, payload?.documents_linked),
    tasksCreated: readConversionNumber(payload?.tasksCreated, payload?.tasks_created),
    templateUsed: readConversionString(payload?.templateUsed, payload?.template_used) || null,
    conversionTimestamp: readConversionString(payload?.conversionTimestamp, payload?.conversion_timestamp) || new Date().toISOString(),
    warnings: Array.isArray(payload?.warnings) ? payload.warnings.filter((warning: unknown): warning is string => typeof warning === 'string') : undefined,
  };
};

export interface ScheduleConsultationRequest {
  start_time: string;
  end_time?: string;
  duration_minutes?: number;
  meeting_mode?: 'phone' | 'video' | 'in_person' | 'court' | 'other';
  location?: string;
  notes?: string;
  include_client_as_attendee?: boolean;
  create_prep_task?: boolean;
}

export interface RequestRetainerRequest {
  requested_amount: number;
  due_date?: string;
  minimum_to_begin?: number;
  notes?: string;
  billing_basis?: 'trust_retainer' | 'flat_fee_advance' | 'hybrid';
  required_for_work_start?: boolean;
  trust_account_id?: string;
  replenish_amount?: number;
}

export interface RecordRetainerPaymentRequest {
  amount: number;
  payment_method?: 'check' | 'wire' | 'ach' | 'cash' | 'credit_card' | 'eft';
  reference_number?: string;
  notes?: string;
  trust_account_id?: string;
  effective_date?: string;
}

// --- Conversion Preview (EF-5.2) ---

export interface MatchedTemplate {
  templateId: string;
  name: string;
  practiceArea: string;
  jurisdiction?: string;
  description?: string;
  taskCount: number;
}

export interface ConversionPreview {
  clientId: string;
  clientName: string;
  clientStatus: string;
  isConvertible: boolean;
  blockingIssues: string[];
  documentCount: number;
  intakeCompleteness: number;
  matchedTemplates: MatchedTemplate[];
  recommendedTemplateId: string | null;
  conversionPlan?: {
    recommendedPracticeArea?: string;
    splitRecommended: boolean;
    potentialMatters: Array<LeadPotentialMatterPlan & {
      matchedTemplateCount: number;
      recommendedTemplateId: string | null;
    }>;
    nextActions: string[];
  };
}

// --- Document Generation Types (EF-7G) ---

export interface AutoFillVariable {
  key: string;
  label: string;
  category: string;
  example: string;
}

export interface IntakeDocumentTemplateOption {
  id: string;
  name: string;
  description: string;
  documentType: 'engagement_letter' | 'fee_agreement' | 'retainer_request' | 'conflict_waiver' | 'ethical_screen' | 'other';
  templateType: string;
  contentFormat: 'html' | 'markdown' | 'lexical' | 'plaintext';
  practiceAreas: string[];
  licensedStates: string[];
  source: 'stub' | 'firm';
  replaceable: boolean;
}

export interface GeneratedDocument {
  template_id: string;
  template_name: string;
  content_format: string;
  filled_content: string;
  variables_resolved: string[];
  variables_unresolved: string[];
  is_stub_template: boolean;
}

export interface DocumentPreview {
  template_id: string;
  template_name: string;
  content_format: string;
  filled_content: string;
  variables_resolved: string[];
  variables_unresolved: string[];
  is_stub_template: boolean;
}

// --- API Client ---

const BASE = '/api/v1/intake';

export const intakeCrmApi = {
  /** List leads with filters */
  listLeads: async (filters: LeadListFilters = {}): Promise<LeadListResult> => {
    const res = await apiClient.get<{ data: LeadListResult }>(`${BASE}/leads`, filters);
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to fetch leads');
    return (res.data as any);
  },

  /** Get lead detail */
  getLeadDetail: async (clientId: string): Promise<LeadDetail> => {
    const res = await apiClient.get<{ data: LeadDetail }>(`${BASE}/leads/${clientId}`);
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to fetch lead detail');
    return (res.data as any);
  },

  /** Move lead to new pipeline stage */
  moveStage: async (clientId: string, stage: string): Promise<void> => {
    const res = await apiClient.patch(`${BASE}/leads/${clientId}/stage`, { stage });
    if (!res.success) throw new Error(res.error || 'Failed to move stage');
  },

  /** Assign lead to attorney */
  assignLead: async (clientId: string, assigneeUserId: string): Promise<void> => {
    const res = await apiClient.post(`${BASE}/leads/${clientId}/assign`, { assignee_user_id: assigneeUserId });
    if (!res.success) throw new Error(res.error || 'Failed to assign lead');
  },

  /** Record a contact attempt */
  recordContact: async (clientId: string, method: string, notes?: string, outcome?: string): Promise<void> => {
    const res = await apiClient.post(`${BASE}/leads/${clientId}/contact`, { method, notes, outcome });
    if (!res.success) throw new Error(res.error || 'Failed to record contact');
  },

  /** Schedule an intake consultation on the assigned attorney calendar */
  scheduleConsultation: async (clientId: string, data: ScheduleConsultationRequest): Promise<LeadDetail> => {
    const res = await apiClient.post<{ data: LeadDetail }>(`${BASE}/leads/${clientId}/consultation`, data);
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to schedule consultation');
    return (res.data as any);
  },

  requestRetainer: async (clientId: string, data: RequestRetainerRequest): Promise<LeadDetail> => {
    const res = await apiClient.post<{ data: LeadDetail }>(`${BASE}/leads/${clientId}/retainer/request`, data);
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to request retainer');
    return (res.data as any);
  },

  recordRetainerPayment: async (clientId: string, data: RecordRetainerPaymentRequest): Promise<LeadDetail> => {
    const res = await apiClient.post<{ data: LeadDetail }>(`${BASE}/leads/${clientId}/retainer/payment`, data);
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to record retainer payment');
    return (res.data as any);
  },

  /** Add a note */
  addNote: async (clientId: string, text: string): Promise<void> => {
    const res = await apiClient.post(`${BASE}/leads/${clientId}/note`, { text });
    if (!res.success) throw new Error(res.error || 'Failed to add note');
  },

  /** Get activity timeline */
  getActivity: async (clientId: string): Promise<LeadActivityEvent[]> => {
    const res = await apiClient.get<{ data: { activity: LeadActivityEvent[] } }>(`${BASE}/leads/${clientId}/activity`);
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to fetch activity');
    return ((res.data as any)?.activity) || [];
  },

  /** Get documents */
  getDocuments: async (clientId: string): Promise<LeadDocument[]> => {
    const res = await apiClient.get<{ data: { documents: LeadDocument[] } }>(`${BASE}/leads/${clientId}/documents`);
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to fetch documents');
    return ((res.data as any)?.documents) || [];
  },

  /** Create lead directly (Quick Intake) */
  createLead: async (data: {
    name: string;
    email?: string;
    phone?: string;
    practice_area?: string;
    practice_areas?: string[];
    notes?: string;
    source?: string;
    preferred_name?: string;
    alternate_phone?: string;
    home_address?: LeadAddress;
    preferred_contact_method?: string;
    preferred_contact_time?: string;
    language?: string;
    interpreter_needed?: boolean;
    date_of_birth?: string;
    marital_status?: string;
    education_level?: string;
    number_of_dependents?: number;
    number_of_kids?: number;
    employed?: boolean;
    employer_name?: string;
    job_title?: string;
    matter_summary?: string;
    state_code?: string;
    jurisdiction_code?: string;
    jurisdiction_court_name?: string;
    jurisdiction?: string;
    incident_date?: string;
    incident_location?: string;
    opposing_party_names?: string;
    current_or_prior_attorney?: string;
    spouse_or_partner_name?: string;
    conflict_parties?: LeadConflictParty[];
    case_modules?: LeadCaseModules;
    matter_plans?: Array<{
      practice_area: string;
      title?: string;
      summary?: string;
      state_code?: string;
      jurisdiction_code?: string;
      jurisdiction_court_name?: string;
      jurisdiction?: string;
      incident_date?: string;
      incident_location?: string;
    }>;
  }): Promise<LeadSummary> => {
    const res = await apiClient.post<{ data: LeadSummary }>(`${BASE}/leads`, data);
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to create lead');
    return (res.data as any);
  },

  updateLeadProfile: async (
    clientId: string,
    data: {
      preferred_name?: string;
      email?: string;
      phone?: string;
      alternate_phone?: string;
      home_address?: LeadAddress;
      preferred_contact_method?: string;
      preferred_contact_time?: string;
      language?: string;
      interpreter_needed?: boolean;
      date_of_birth?: string;
      marital_status?: string;
      education_level?: string;
      number_of_dependents?: number;
      number_of_kids?: number;
      employed?: boolean;
      employer_name?: string;
      job_title?: string;
      practice_area?: string;
      practice_areas?: string[];
      matter_summary?: string;
      state_code?: string;
      jurisdiction_code?: string;
      jurisdiction_court_name?: string;
      jurisdiction?: string;
      incident_date?: string;
      incident_location?: string;
      opposing_party_names?: string;
      current_or_prior_attorney?: string;
      spouse_or_partner_name?: string;
      conflict_parties?: LeadConflictParty[];
      case_modules?: LeadCaseModules;
      matter_plans?: Array<{
        practice_area: string;
        title?: string;
        summary?: string;
        state_code?: string;
        jurisdiction_code?: string;
        jurisdiction_court_name?: string;
        jurisdiction?: string;
        incident_date?: string;
        incident_location?: string;
      }>;
    }
  ): Promise<LeadDetail> => {
    const res = await apiClient.patch<{ data: LeadDetail }>(`${BASE}/leads/${clientId}/profile`, data);
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to update lead profile');
    return (res.data as any);
  },

  listJurisdictionStates: async (): Promise<CourtRegistryState[]> => {
    const res = await apiClient.get<{ states: CourtRegistryState[] }>('/api/v1/court-registry/states');
    return ((res as any)?.states || []) as CourtRegistryState[];
  },

  listCourtsByState: async (stateCode: string): Promise<CourtRegistryCourt[]> => {
    const res = await apiClient.get<{ courts: CourtRegistryCourt[] }>('/api/v1/court-registry/courts', { state_code: stateCode });
    return ((res as any)?.courts || []) as CourtRegistryCourt[];
  },

  /** Get pipeline metrics */
  getMetrics: async (): Promise<PipelineMetrics> => {
    const res = await apiClient.get<{ data: PipelineMetrics }>(`${BASE}/leads/metrics`);
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to fetch metrics');
    return (res.data as any);
  },

  /** Get conversion preview (EF-5.2) */
  getConversionPreview: async (clientId: string, practiceArea?: string, jurisdiction?: string): Promise<ConversionPreview> => {
    const params: Record<string, string> = {};
    if (practiceArea) params.practice_area = practiceArea;
    if (jurisdiction) params.jurisdiction = jurisdiction;
    const res = await apiClient.get<{ data: ConversionPreview }>(`${BASE}/clients/${clientId}/convert/preview`, params);
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to fetch conversion preview');
    return (res.data as any);
  },

  /** Convert lead to client (calls EF-1 conversion endpoint) */
  convertLead: async (clientId: string, data: ConversionRequest): Promise<ConversionResult> => {
    const res = await apiClient.post<{ data: ConversionResult }>(`${BASE}/clients/${clientId}/convert`, data);
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to convert lead');
    return normalizeConversionResult(res.data);
  },

  /** Get auto-fill variables for document generation (EF-7G) */
  getAutoFillVariables: async (): Promise<AutoFillVariable[]> => {
    const res = await apiClient.get<{ data: { variables: AutoFillVariable[] } }>(`${BASE}/leads/autofill-variables`);
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to fetch auto-fill variables');
    return ((res.data as any)?.variables) || [];
  },

  /** List intake-safe document templates, including replaceable stubs */
  getDocumentTemplates: async (): Promise<IntakeDocumentTemplateOption[]> => {
    const res = await apiClient.get<{ data: { templates: IntakeDocumentTemplateOption[] } }>(`${BASE}/leads/document-templates`);
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to fetch document templates');
    return ((res.data as any)?.templates) || [];
  },

  /** Generate document from template with auto-filled variables (EF-7G) */
  generateDocument: async (clientId: string, templateId: string): Promise<GeneratedDocument> => {
    const res = await apiClient.post<{ data: GeneratedDocument }>(`${BASE}/leads/${clientId}/documents/generate`, { template_id: templateId });
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to generate document');
    return (res.data as any);
  },

  /** Preview document auto-fill without generating (EF-7G) */
  previewDocument: async (clientId: string, templateId: string): Promise<DocumentPreview> => {
    const res = await apiClient.post<{ data: DocumentPreview }>(`${BASE}/leads/${clientId}/documents/preview`, { template_id: templateId });
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to preview document');
    return (res.data as any);
  },

  /** Update checklist item for a lead (EF-7F) */
  updateChecklist: async (clientId: string, itemId: string, checked: boolean): Promise<void> => {
    const res = await apiClient.patch(`${BASE}/leads/${clientId}/checklist`, { item_id: itemId, checked });
    if (!res.success) throw new Error(res.error || 'Failed to update checklist');
  },

  runConflictCheck: async (clientId: string): Promise<LeadDetail> => {
    const res = await apiClient.post<{ data: LeadDetail }>(`${BASE}/leads/${clientId}/conflict-check/run`);
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to run conflict check');
    return res.data as any;
  },

  requestConflictReview: async (clientId: string, note?: string): Promise<LeadDetail> => {
    const res = await apiClient.post<{ data: LeadDetail }>(`${BASE}/leads/${clientId}/conflict-check/request-review`, { note });
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to request conflict review');
    return res.data as any;
  },

  recordConflictDisposition: async (
    clientId: string,
    disposition: 'clear' | 'waiver_required' | 'screen_required' | 'declined' | 'blocked',
    note?: string,
  ): Promise<LeadDetail> => {
    const res = await apiClient.post<{ data: LeadDetail }>(`${BASE}/leads/${clientId}/conflict-check/disposition`, {
      disposition,
      note,
    });
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to record conflict disposition');
    return res.data as any;
  },

  runExternalConflictEnrichment: async (clientId: string): Promise<LeadDetail> => {
    const res = await apiClient.post<{ data: LeadDetail }>(`${BASE}/leads/${clientId}/conflict-check/external-enrichment`);
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to run external conflict enrichment');
    return res.data as any;
  },
};
